import { useState, useRef, useEffect } from 'react';
import { getS3DownloadUrl } from '@/services/data/s3Files';
import { logger } from '@/utils/logger';

export function useInlineVideoPlayer(videoKeys: string[], eventId: string | undefined) {
  const inlineVideoRef = useRef<HTMLVideoElement>(null);
  const [inlinePlaying, setInlinePlaying] = useState(false);
  const [inlineMuted, setInlineMuted] = useState(true);
  const [inlineCurrentTime, setInlineCurrentTime] = useState(0);
  const [inlineDuration, setInlineDuration] = useState(0);
  const [inlineActiveVersion, setInlineActiveVersion] = useState(0);
  const [resolvedVideoUrls, setResolvedVideoUrls] = useState<Record<string, string>>({});

  // Resolve S3 keys to signed URLs
  useEffect(() => {
    if (videoKeys.length === 0) return;
    let cancelled = false;
    const resolve = async () => {
      const entries: Record<string, string> = {};
      await Promise.all(
        videoKeys.map(async (key) => {
          if (key.startsWith('http') || key.startsWith('blob:')) {
            entries[key] = key;
          } else {
            const result = await getS3DownloadUrl(key, false);
            if (result.success && result.data?.url) {
              entries[key] = result.data.url;
            }
          }
        }),
      );
      if (!cancelled) setResolvedVideoUrls(entries);
    };
    resolve();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Reset video when version changes
  useEffect(() => {
    const video = inlineVideoRef.current;
    if (video) {
      video.currentTime = 0;
      setInlineCurrentTime(0);
      setInlinePlaying(false);
    }
  }, [inlineActiveVersion]);

  const inlineActiveUrl = resolvedVideoUrls[videoKeys[inlineActiveVersion]] ?? null;

  const toggleInlinePlay = () => {
    const video = inlineVideoRef.current;
    if (!video) return;
    if (inlinePlaying) video.pause();
    else video.play().catch((err) => logger.error('[InlineVideoPlayer] play() failed:', err));
  };

  const toggleInlineMute = () => {
    const video = inlineVideoRef.current;
    if (video) video.muted = !inlineMuted;
    setInlineMuted(!inlineMuted);
  };

  const handleSeekToInline = (time: number) => {
    const v = inlineVideoRef.current;
    if (v) v.currentTime = time;
  };

  const toggleInlinePlayForFeedback = () => {
    const v = inlineVideoRef.current;
    if (!v) return;
    if (inlinePlaying) v.pause();
    else v.play().catch((err) => logger.error('[InlineVideoPlayer] play() failed:', err));
  };

  const formatVideoTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return {
    inlineVideoRef,
    inlinePlaying,
    setInlinePlaying,
    inlineMuted,
    inlineCurrentTime,
    setInlineCurrentTime,
    inlineDuration,
    setInlineDuration,
    inlineActiveVersion,
    setInlineActiveVersion,
    inlineActiveUrl,
    toggleInlinePlay,
    toggleInlineMute,
    handleSeekToInline,
    toggleInlinePlayForFeedback,
    formatVideoTime,
  };
}
